import { ReactNode } from "react";
import { Box, Center, Divider, Flex, Text } from "@chakra-ui/react";
import SimplePanel from "components/Shared/Panel/Simple";
import { FragmentType, getFragmentData, gql } from "__generated__/gql";
import RefreshButton from "components/Shared/RefreshButton";
import { PANEL_PADDING } from "components/Shared/Panel";
import LoadingSpinner from "components/Shared/PageLoadingSpinner";
import MaxHeightExpander from "components/Shared/MaxHeightExpander";
import SelectedEmailContent from "./Content";
import DownloadOriginal from "./DownloadOriginal";
import PreviewHeader from "./PreviewHeader";
import EmailAttachments from "./EmailAttachments";

const fragment = gql(/* GraphQL */ `
  fragment SelectedEmail on ReceivedEmailMessage {
    emailMessageId
    emailMessage {
      id
      subject
    }
    parsedReceivedEmailMessage {
      receivedEmailMessageId
    }
    ...SelectedEmailPreviewHeader
    ...SelectedEmailDownloadOriginal
    ...SelectedEmailAttachments
  }
`);

type Props = {
  actions?: ReactNode;
  data: FragmentType<typeof fragment> | null | undefined;
  filenamePrefix?: string;
  isLoading?: boolean;
  isReceiverShowAllToggleEnabled?: boolean;
  onRefresh?: () => void;
};

export default function SelectedEmail({
  actions,
  data,
  filenamePrefix,
  isLoading,
  isReceiverShowAllToggleEnabled = false,
  onRefresh,
}: Props) {
  const rem = getFragmentData(fragment, data);

  return (
    <SimplePanel
      heading={rem?.emailMessage?.subject}
      header={
        <Flex ml="auto" gap={1}>
          <RefreshButton size="sm" onClick={onRefresh} />
          <DownloadOriginal data={rem} filenamePrefix={filenamePrefix} />
        </Flex>
      }
      minW="fit-content"
    >
      <Flex
        flexWrap="wrap"
        gap={6}
        overflow="hidden"
        sx={{ "> *": { borderLeftWidth: 1, ml: "calc(-1px - .75rem)", pl: 3 } }}
      >
        <Box flex="8 1 20rem" minW="16rem">
          {rem && (
            <MaxHeightExpander maxHeight={150} mt={-2} mb={3}>
              <PreviewHeader
                data={rem}
                isLoading={isLoading}
                isReceiverShowAllToggleEnabled={isReceiverShowAllToggleEnabled}
                isSubjectHidden
              />
            </MaxHeightExpander>
          )}

          <Divider />

          {isLoading && <LoadingSpinner p={10} />}

          {rem?.parsedReceivedEmailMessage?.receivedEmailMessageId && (
            <>
              <EmailAttachments data={rem} />
              <Box ml={-PANEL_PADDING} mr={-3} mb={-PANEL_PADDING}>
                <SelectedEmailContent
                  key={rem.emailMessageId}
                  emailId={rem.emailMessageId}
                />
              </Box>
            </>
          )}

          {!rem?.parsedReceivedEmailMessage?.receivedEmailMessageId &&
            !isLoading && (
              <Center p={10}>
                <Text color="gray.500">
                  Email contents not available yet. Please check back later.
                </Text>
              </Center>
            )}
        </Box>

        {actions && (
          <Box flex="1 0 16rem" minW="16rem">
            {actions}
          </Box>
        )}
      </Flex>
    </SimplePanel>
  );
}
